import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import type { FirebaseError } from "firebase/app";
import { Loader2 } from "lucide-react";
import { auth, googleProvider } from "@/lib/firebase";
import { exchangeFirebaseToken } from "@/features/auth/lib/exchange";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { getDefaultRoute } from "@/features/auth/lib/page-routes";
import { toast } from "sonner";
import { Sentry } from "@/lib/sentry";
import { GoogleIcon } from "@/components/icons/GoogleIcon";
import {
  AdgeLogo,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PasswordInput,
} from "@nexus/ui";

const NOT_PROVISIONED_MARKER = "User not found";

export function LoginForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lastVisitedRoute = useAuth((s) => s.lastVisitedRoute);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  async function handleExchangeAndNavigate(
    firebaseToken: string,
    extra?: { picture?: string | null; name?: string | null },
  ) {
    let defaultRoute: string | null;
    try {
      const { tenants } = await exchangeFirebaseToken(firebaseToken, extra);
      defaultRoute = getDefaultRoute(tenants, lastVisitedRoute);
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (message.includes(NOT_PROVISIONED_MARKER)) {
        toast.error(t("auth.accountNotProvisioned"));
      } else {
        Sentry.captureException(err, { tags: { flow: "auth-exchange" } });
        toast.error(t("auth.exchangeError"));
      }
      throw err;
    }
    // Fire navigation without awaiting — the router transitions immediately
    // and the destination route's `pendingComponent` (e.g. the dashboard
    // skeleton) renders inside the AppShell while its loader runs. Awaiting
    // here would keep the LoginForm mounted with its spinner for the full
    // duration of the loader, which is the bug we're fixing.
    void navigate({ to: defaultRoute ?? "/login" });
  }

  async function handlePasswordLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email || !password) return;
    setFieldError(null);
    setLoading(true);

    try {
      let credential;
      try {
        credential = await signInWithEmailAndPassword(auth, email, password);
      } catch {
        setFieldError(t("auth.loginError"));
        return;
      }

      const firebaseToken = await credential.user.getIdToken(true);
      await handleExchangeAndNavigate(firebaseToken);
    } catch {
      // Exchange errors already toasted in handleExchangeAndNavigate
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleLogin() {
    setGoogleLoading(true);
    try {
      let credential;
      try {
        credential = await signInWithPopup(auth, googleProvider);
      } catch (err) {
        const firebaseErr = err as FirebaseError;
        const code = firebaseErr.code;
        if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
          return;
        }
        console.error("[Google sign-in]", code, firebaseErr.message);
        Sentry.captureException(err, { tags: { flow: "google-signin", errorCode: code } });
        toast.error(t("auth.googleError"));
        return;
      }

      const firebaseToken = await credential.user.getIdToken(true);
      await handleExchangeAndNavigate(firebaseToken, {
        picture: credential.user.photoURL,
        name: credential.user.displayName,
      });
    } catch {
      // Exchange errors already toasted
    } finally {
      setGoogleLoading(false);
    }
  }

  const anyLoading = loading || googleLoading;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--background)] p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-1 text-start">
          <AdgeLogo className="mb-2 origin-start" aria-label={t("auth.brandName")} />
          <CardTitle className="text-xl font-normal">{t("auth.title")}</CardTitle>
          <CardDescription className="text-sm">{t("auth.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handlePasswordLogin} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t("auth.emailPlaceholder", "you@company.com")}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (fieldError) setFieldError(null);
                }}
                required
                autoComplete="email"
                disabled={anyLoading}
                aria-invalid={fieldError ? true : undefined}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <Link
                  to="/auth/forgot-password"
                  className="text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)] underline-offset-4 hover:underline"
                >
                  {t("auth.forgotPassword")}
                </Link>
              </div>
              <PasswordInput
                id="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (fieldError) setFieldError(null);
                }}
                required
                autoComplete="current-password"
                disabled={anyLoading}
                aria-invalid={fieldError ? true : undefined}
                aria-describedby={fieldError ? "login-error" : undefined}
                showPasswordLabel={t("auth.showPassword")}
                hidePasswordLabel={t("auth.hidePassword")}
              />
              {fieldError && (
                <p id="login-error" role="alert" className="text-xs text-[var(--destructive)]">
                  {fieldError}
                </p>
              )}
            </div>
            <Button type="submit" className="w-full gap-2" disabled={anyLoading} aria-busy={loading}>
              {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              <span>{loading ? t("auth.signingIn") : t("auth.signIn")}</span>
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-[var(--card)] px-2 text-[var(--muted-foreground)]">
                {t("auth.orDivider")}
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full gap-2 border-[#dadce0] bg-white text-[#3c4043] hover:bg-[#f8f9fa] hover:text-[#3c4043] dark:border-[var(--border)] dark:bg-[var(--card)] dark:text-[var(--foreground)] dark:hover:bg-[var(--accent)]"
            onClick={handleGoogleLogin}
            disabled={anyLoading}
            aria-label={t("auth.signInWithGoogle")}
            aria-busy={googleLoading}
          >
            {googleLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <GoogleIcon className="h-4 w-4" />
            )}
            <span>{googleLoading ? t("auth.openingGoogle") : t("auth.signInWithGoogle")}</span>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
